import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCinematicSlides } from '../hooks/useCinematicSlides';
import { getSessionToken } from '../utils/auth';
import { hasSeenSlideshow, markSlideshowSeen } from '../utils/seenState';
import '../styles/public.css';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('landing');
  const { current, slideClass, advance, retreat, goTo } = useCinematicSlides(3, 5500, 2);

  useEffect(() => {
    if (getSessionToken()) {
      navigate('/dashboard', { replace: true });
      return;
    }
    if (hasSeenSlideshow()) {
      navigate('/home', { replace: true });
      return;
    }
    markSlideshowSeen();
  }, [navigate]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') advance();
      if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   retreat();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [advance, retreat]);

  return (
    <div className="cin-shell">

      {/* ── Slide 1: Welcome ─────────────────────────────── */}
      <div className={slideClass(0)}>
        <div className="cin-bg cin-bg--globe">
          <div className="cin-stars" />
          <div className="cin-orb" />
          <div className="cin-overlay" />
        </div>
        <div className="cin-content">
          <div className="cin-inner">
            <div className="cin-logo">
              <span className="cin-logo-n">Nomad</span>
              <span className="cin-logo-m">Me</span>
            </div>
            <span className="cin-label">{t('landingPage.slide1Label')}</span>
            <h1 className="cin-headline">
              {t('landingPage.slide1Headline')}
            </h1>
            <p className="cin-subline">
              {t('landingPage.slide1Sub')}
            </p>
            <div className="cin-gap--sm" />
            <button className="cin-action-btn cin-action-btn--gold" onClick={advance}>
              {t('landingPage.slide1Cta')} &rsaquo;
            </button>
          </div>
        </div>
      </div>

      {/* ── Slide 2: What we do ──────────────────────────── */}
      <div className={slideClass(1)}>
        <div className="cin-bg cin-bg--dark">
          <div className="cin-stars" />
          <div className="cin-overlay cin-overlay--heavy" />
        </div>
        <div className="cin-content">
          <div className="cin-inner">
            <span className="cin-label">{t('landingPage.slide2Label')}</span>
            <h1 className="cin-headline">
              {t('landingPage.slide2Headline')}
            </h1>
            <p className="cin-subline">
              {t('landingPage.slide2Sub')}
            </p>
            <div className="cin-gap--sm" />
            <div className="cin-stat-row">
              <div className="cin-stat">
                <div className="cin-stat-num">30+</div>
                <div className="cin-stat-label">{t('landingPage.slide2Stat1Label')}</div>
              </div>
              <div className="cin-stat">
                <div className="cin-stat-num">5</div>
                <div className="cin-stat-label">{t('landingPage.slide2Stat2Label')}</div>
              </div>
              <div className="cin-stat">
                <div className="cin-stat-num">20–30%</div>
                <div className="cin-stat-label">{t('landingPage.slide2Stat3Label')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Slide 3: Choose destination ──────────────────── */}
      <div className={slideClass(2)}>
        <div className="cin-bg" style={{ background: 'radial-gradient(ellipse at 30% 50%, rgba(34,197,94,0.09) 0%, transparent 50%), radial-gradient(ellipse at 70% 50%, rgba(239,68,68,0.09) 0%, transparent 50%), #05060d' }}>
          <div className="cin-stars" />
          <div className="cin-overlay" style={{ background: 'rgba(5,6,13,0.3)' }} />
        </div>
        <div className="cin-content">
          <div className="cin-inner">
            <span className="cin-label">{t('landingPage.slide3Label')}</span>
            <h1 className="cin-headline">{t('landingPage.slide3Headline')}</h1>
            <p className="cin-subline">
              {t('landingPage.slide3Sub')}
            </p>
            <div className="cin-gap--sm" />
            <div className="cin-dest-row">
              <Link to="/africa" className="cin-dest-btn cin-dest-btn--africa">
                <span className="cin-dest-btn-emoji">🌿</span>
                Africa
              </Link>
              <Link to="/china" className="cin-dest-btn cin-dest-btn--china">
                <span className="cin-dest-btn-emoji">🏮</span>
                China
              </Link>
            </div>
            <div className="cin-gap--sm" />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginTop: 8 }}>
              <Link to="/home" className="cin-action-btn cin-action-btn--ghost" style={{ fontSize: '0.8rem', padding: '10px 22px' }}>
                🏠 {t('appNav.home')}
              </Link>
              <Link to="/globe" className="cin-action-btn cin-action-btn--ghost" style={{ fontSize: '0.8rem', padding: '10px 22px' }}>
                🌐 {t('appNav.globe')}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ── Shared controls ──────────────────────────────── */}
      <div className="cin-controls">
        <button
          className={`cin-arrow cin-arrow--left${current === 0 ? ' cin-arrow--hidden' : ''}`}
          onClick={retreat}
          aria-label="Previous"
        >
          ‹
        </button>
        <button
          className={`cin-arrow cin-arrow--right${current === 2 ? ' cin-arrow--hidden' : ''}`}
          onClick={advance}
          aria-label="Next"
        >
          ›
        </button>
        <div className="cin-dots">
          {[0, 1, 2].map((i) => (
            <button
              key={i}
              className={`cin-dot${current === i ? ' cin-dot--active' : ''}`}
              onClick={() => goTo(i)}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
